/**
 * useVisualViewport — the height and top offset of the part of the page the on-screen
 * keyboard leaves visible.
 *
 * A region that holds a text input takes this top and height, so its bottom edge sits
 * on top of the keyboard on iOS and Android. The layout viewport (`100dvh`) doesn't
 * shrink for the keyboard on iOS, which is why the shell's height isn't enough.
 */

import { useEffect, useState } from 'react';

interface UseVisualViewportReturn {
  height: number;
  /** How far the visual viewport has scrolled down the page, as iOS does for a focused input. */
  offsetTop: number;
}

function readVisualViewport(): UseVisualViewportReturn {
  const viewport = window.visualViewport;
  return {
    height: viewport?.height ?? window.innerHeight,
    offsetTop: viewport?.offsetTop ?? 0,
  };
}

export function useVisualViewport(): UseVisualViewportReturn {
  const [state, setState] = useState(readVisualViewport);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleChange = () => setState(readVisualViewport());
    viewport.addEventListener('resize', handleChange);
    viewport.addEventListener('scroll', handleChange);
    return () => {
      viewport.removeEventListener('resize', handleChange);
      viewport.removeEventListener('scroll', handleChange);
    };
  }, []);

  return state;
}
